import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ParseKeys } from "i18next";
import { Box } from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";

import Tabs from "components/basic/tabs";
import Button from "components/basic/button";
import LoadingSpinner from "components/basic/loadingSpinner";
import Scrollbar from "components/basic/scrollbar";
import useUrlParamState from "hooks/useUrlParamState";

import NewTicketDialog from "./components/NewTicketDialog";
import TicketDetail from "./components/TicketDetail";
import TicketList from "./components/TicketList";
import { SUPPORT_TABS } from "./constants";
import { filterByTab } from "./helpers";
import { useTickets } from "./supportApi";
import { TabKey } from "./types";

const EMPTY_TEXT: Record<TabKey, ParseKeys<"support">> = {
  open: "empty.open",
  resolved: "empty.resolved",
  all: "empty.all",
};

/** A user's or vendor's own support tickets. */
const Support = () => {
  const { t } = useTranslation("support");
  const [selectedTab, setSelectedTab] = useState<TabKey>("open");
  const [ticketParam, setTicketParam] = useUrlParamState("ticket");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { tickets, loading } = useTickets(false);

  const selectedId = ticketParam ? Number(ticketParam) : null;

  if (selectedId) {
    return (
      <TicketDetail
        key={selectedId}
        ticketId={selectedId}
        onBack={() => setTicketParam(null)}
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Tabs
        selectedTab={selectedTab}
        setSelectedTab={(tab) => setSelectedTab(tab as TabKey)}
        tabs={SUPPORT_TABS.map((tab) => ({ ...tab, label: t(tab.label) }))}
      />

      <Box display="flex" justifyContent="flex-end" px={2} py={1}>
        <Button
          size="small"
          startAdornment={<AddCommentOutlinedIcon fontSize="small" />}
          onClick={() => setDialogOpen(true)}
        >
          {t("new_ticket")}
        </Button>
      </Box>

      <Scrollbar sx={{ flex: 1, minHeight: 0 }} showHint={false}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <LoadingSpinner />
          </Box>
        ) : (
          <TicketList
            tickets={filterByTab(tickets, selectedTab)}
            emptyText={t(EMPTY_TEXT[selectedTab])}
            onSelect={(ticket) => setTicketParam(String(ticket.id))}
          />
        )}
      </Scrollbar>

      {dialogOpen && (
        <NewTicketDialog
          onClose={() => setDialogOpen(false)}
          onCreated={(ticketId) => {
            setDialogOpen(false);
            setTicketParam(String(ticketId));
          }}
        />
      )}
    </Box>
  );
};

export default Support;
